import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Place as PlaceIcon,
  LocalPharmacy as LocalPharmacyIcon,
  EventBusy as EventBusyIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { monitoramentoService } from '../../services/api';
import KpiCard from '../../components/monitoramento/KpiCard';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

// Leaflet
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { getTileUrl, getTileAttribution } from '../../utils/map-utils';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

// ---------- INTERFACES ----------

interface UnidadeRow {
  nome_unidade: string;
  total_dispensacoes: number;
  custo_total: number;
  pacientes: number;
}

interface EvolucaoRow {
  mes: string;
  custo_total: number;
}

interface MedicamentoRow {
  nome_medicamento: string;
  total_quantidade: number;
}

interface DispensacoesData {
  kpis: {
    total_dispensacoes: number;
    custo_total: number;
    pacientes_atendidos: number;
    produtos_distintos: number;
  };
  topMedicamentos: MedicamentoRow[];
  evolucao: EvolucaoRow[];
  porUnidade: UnidadeRow[];
}

interface UnidadeEstoque {
  codigo_unidade: number;
  nome_unidade: string;
  endereco: string;
  bairro: string;
  telefone: string;
  horario_funcionamento: string;
  latitude: number;
  longitude: number;
  qtd_produtos_adequados: number;
  qtd_produtos_baixo: number;
  qtd_produtos_falta: number;
  qtd_lotes_vencendo: number;
  qtd_lotes_vencidos: number;
  proximo_vencimento: string | null;
  status_geral: 'ADEQUADO' | 'BAIXO' | 'FALTA';
}

interface LoteRow {
  id: number;
  codigo_unidade: number;
  nome_unidade?: string;
  nome_medicamento: string;
  lote: string;
  fabricante: string;
  quantidade_disponivel: number;
  data_validade: string;
  data_fabricacao?: string;
  situacao: string;
  dias_para_vencer: number;
}

interface EstoqueData {
  kpis: {
    total_unidades: number;
    unidades_criticidade: number;
    lotes_vencendo: number;
    lotes_vencidos: number;
  };
  unidades: UnidadeEstoque[];
  lotesAlerta: LoteRow[];
}

interface PcdtRef {
  id: number;
  codigo: string;
  titulo: string;
  doenca_cid: string;
}

interface RemumeRow {
  remume_id: number;
  codigo_produto: string;
  nome: string;
  principio_ativo: string;
  classe_terapeutica: string;
  componente: 'BASICO' | 'ESTRATEGICO' | 'ESPECIALIZADO';
  unidades_com_estoque: number;
  total_unidades: number;
  percentual_cobertura: number;
  status_cobertura: 'TOTAL' | 'PARCIAL' | 'SEM_ESTOQUE';
  pcdts: PcdtRef[];
}

interface PcdtMedicamento {
  remume_id: number;
  codigo_produto: string;
  nome: string;
  componente: string;
  status_cobertura: 'TOTAL' | 'PARCIAL' | 'SEM_ESTOQUE';
  percentual_cobertura: number;
}

interface PcdtRow {
  id: number;
  codigo: string;
  titulo: string;
  doenca_cid: string;
  vigencia_inicio: string;
  vigencia_fim: string | null;
  nivel_atencao: string;
  total_medicamentos: number;
  medicamentos_disponiveis: number;
  medicamentos: PcdtMedicamento[];
}

interface CoberturaData {
  kpis: {
    total_remume: number;
    com_estoque_total: number;
    com_estoque_parcial: number;
    sem_estoque: number;
  };
  medicamentos: RemumeRow[];
  pcdts: PcdtRow[];
}

// ---------- HELPERS ----------

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

const CORES_STATUS: Record<string, string> = {
  ADEQUADO: '#2E7D32',
  BAIXO: '#F39C12',
  FALTA: '#E74C3C',
};

const CORES_COBERTURA: Record<string, string> = {
  TOTAL: '#2E7D32',
  PARCIAL: '#F39C12',
  SEM_ESTOQUE: '#E74C3C',
};

const LABEL_COBERTURA: Record<string, string> = {
  TOTAL: 'Cobertura total',
  PARCIAL: 'Cobertura parcial',
  SEM_ESTOQUE: 'Sem estoque',
};

function makeStatusIcon(color: string) {
  return new L.DivIcon({
    className: '',
    html: `<div style="width:24px;height:24px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
}

const ICONS_STATUS: Record<string, L.DivIcon> = {
  ADEQUADO: makeStatusIcon(CORES_STATUS.ADEQUADO),
  BAIXO: makeStatusIcon(CORES_STATUS.BAIXO),
  FALTA: makeStatusIcon(CORES_STATUS.FALTA),
};

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('pt-BR');
};

// ============================================================
// ABA 1 - Dispensacoes (conteudo atual, intacto)
// ============================================================
const AbaDispensacoes: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<DispensacoesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultFim = new Date();
  const defaultInicio = new Date();
  defaultInicio.setMonth(defaultInicio.getMonth() - 6);

  const [dataInicio, setDataInicio] = useState(toISO(defaultInicio));
  const [dataFim, setDataFim] = useState(toISO(defaultFim));

  const handleDateChange = useCallback((di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  }, []);

  useEffect(() => {
    if (!municipioAtivo) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getDispensacoes(municipioAtivo.id, dataInicio, dataFim);
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de dispensacoes.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [municipioAtivo, dataInicio, dataFim]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  const kpis = data?.kpis;
  const topMedicamentos = (data?.topMedicamentos || []).map((m) => ({
    ...m,
    nome_medicamento_truncated: m.nome_medicamento.length > 30
      ? m.nome_medicamento.substring(0, 30) + '...'
      : m.nome_medicamento,
  }));
  const evolucao = data?.evolucao || [];
  const porUnidade = data?.porUnidade || [];

  return (
    <Box>
      {/* Date Selector */}
      <Box sx={{ mb: 3 }}>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Total Dispensacoes" valor={(kpis?.total_dispensacoes ?? 0).toLocaleString('pt-BR')} cor="#1565c0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Custo Total" valor={formatCurrency(kpis?.custo_total ?? 0)} cor="#2e7d32" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Pacientes Atendidos" valor={(kpis?.pacientes_atendidos ?? 0).toLocaleString('pt-BR')} cor="#e65100" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Produtos Distintos" valor={(kpis?.produtos_distintos ?? 0).toLocaleString('pt-BR')} cor="#6a1b9a" />
        </Grid>
      </Grid>

      {/* Top Medicamentos */}
      {topMedicamentos.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Top Medicamentos por Quantidade
          </Typography>
          <ResponsiveContainer width="100%" height={Math.max(350, topMedicamentos.length * 40)}>
            <BarChart data={topMedicamentos} layout="vertical" margin={{ left: 180 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="nome_medicamento_truncated" tick={{ fontSize: 11 }} width={170} />
              <Tooltip
                formatter={(value: any) => Number(value).toLocaleString('pt-BR')}
                labelFormatter={(label: any) => {
                  const item = topMedicamentos.find((m) => m.nome_medicamento_truncated === label);
                  return item?.nome_medicamento || label;
                }}
              />
              <Bar dataKey="total_quantidade" name="Quantidade" fill="#1565c0" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Evolucao Custo */}
      {evolucao.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Evolucao Custo Total Mensal
          </Typography>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={evolucao}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(value: number) => formatCurrency(value)} />
              <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
              <Legend />
              <Line type="monotone" dataKey="custo_total" name="Custo Total" stroke="#2e7d32" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Por Unidade */}
      {porUnidade.length > 0 && (
        <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600}>Dispensacoes por Unidade</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Unidade</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Dispensacoes</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Custo Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Pacientes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {porUnidade.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{row.nome_unidade}</TableCell>
                    <TableCell align="right">{row.total_dispensacoes.toLocaleString('pt-BR')}</TableCell>
                    <TableCell align="right">{formatCurrency(row.custo_total)}</TableCell>
                    <TableCell align="right">{row.pacientes.toLocaleString('pt-BR')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {!kpis && topMedicamentos.length === 0 && evolucao.length === 0 && porUnidade.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhum dado disponivel para dispensacoes no periodo selecionado.
          </Typography>
        </Box>
      )}

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="dispensacoes"
          contexto="dashboard"
          manualTrigger
        />
      )}
    </Box>
  );
};

// ============================================================
// ABA 2 - Estoque & Lotes (RF-19.1)
// ============================================================
const AbaEstoqueLotes: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<EstoqueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState<UnidadeEstoque | null>(null);
  const [lotesUnidade, setLotesUnidade] = useState<LoteRow[]>([]);
  const [loadingLotes, setLoadingLotes] = useState(false);

  useEffect(() => {
    if (!municipioAtivo) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await monitoramentoService.getDispensacoesEstoqueUnidades(municipioAtivo.id);
        setData(result);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Erro ao carregar dados de estoque.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [municipioAtivo]);

  const handleVerLotes = async (unidade: UnidadeEstoque) => {
    if (!municipioAtivo) return;
    setUnidadeSelecionada(unidade);
    setLoadingLotes(true);
    try {
      const result = await monitoramentoService.getDispensacoesLotes(municipioAtivo.id, unidade.codigo_unidade);
      setLotesUnidade(result.lotes || []);
    } catch (err) {
      setLotesUnidade([]);
    } finally {
      setLoadingLotes(false);
    }
  };

  const limparFiltroUnidade = () => {
    setUnidadeSelecionada(null);
    setLotesUnidade([]);
  };

  const comCoordenadas = useMemo(
    () => (data?.unidades || []).filter((u) => u.latitude && u.longitude),
    [data],
  );

  const mapCenter = useMemo<[number, number]>(() => {
    if (!comCoordenadas.length) return [-19.93, -44.05];
    const lat = comCoordenadas.reduce((s, u) => s + Number(u.latitude), 0) / comCoordenadas.length;
    const lng = comCoordenadas.reduce((s, u) => s + Number(u.longitude), 0) / comCoordenadas.length;
    return [lat, lng];
  }, [comCoordenadas]);

  if (loading) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={480} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  const kpis = data?.kpis;
  const lotesParaTabela: LoteRow[] = unidadeSelecionada ? lotesUnidade : (data?.lotesAlerta || []);

  const corChip = (dias: number) => {
    if (dias < 0) return { bg: '#E74C3C', label: 'Vencido' };
    if (dias <= 30) return { bg: '#F39C12', label: `${dias}d` };
    if (dias <= 90) return { bg: '#F1C40F', label: `${dias}d` };
    return { bg: '#27AE60', label: `${dias}d` };
  };

  return (
    <Box>
      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Unidades Monitoradas" valor={(kpis?.total_unidades ?? 0).toLocaleString('pt-BR')} cor="#1565c0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Unidades em Criticidade" valor={(kpis?.unidades_criticidade ?? 0).toLocaleString('pt-BR')} cor="#E74C3C" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Lotes Vencendo (90d)" valor={(kpis?.lotes_vencendo ?? 0).toLocaleString('pt-BR')} cor="#F39C12" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Lotes Vencidos" valor={(kpis?.lotes_vencidos ?? 0).toLocaleString('pt-BR')} cor="#6a1b9a" />
        </Grid>
      </Grid>

      {/* Mapa */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaceIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>Painel por Unidade Dispensadora</Typography>
        </Box>
        <Box sx={{ position: 'relative' }}>
          <MapContainer
            center={mapCenter}
            zoom={12}
            style={{ height: 480, width: '100%' }}
            scrollWheelZoom
          >
            <TileLayer url={getTileUrl()} attribution={getTileAttribution()} />
            {comCoordenadas.map((u) => (
              <Marker
                key={u.codigo_unidade}
                position={[Number(u.latitude), Number(u.longitude)]}
                icon={ICONS_STATUS[u.status_geral] || ICONS_STATUS.ADEQUADO}
              >
                <Popup>
                  <Box sx={{ minWidth: 240 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {u.nome_unidade}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {u.endereco}{u.bairro ? ` - ${u.bairro}` : ''}
                    </Typography>
                    {u.telefone && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {u.telefone}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`Adequado: ${u.qtd_produtos_adequados}`} sx={{ bgcolor: CORES_STATUS.ADEQUADO, color: '#fff' }} />
                      <Chip size="small" label={`Baixo: ${u.qtd_produtos_baixo}`} sx={{ bgcolor: CORES_STATUS.BAIXO, color: '#fff' }} />
                      <Chip size="small" label={`Falta: ${u.qtd_produtos_falta}`} sx={{ bgcolor: CORES_STATUS.FALTA, color: '#fff' }} />
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Proximo vencimento: <b>{formatDate(u.proximo_vencimento)}</b>
                    </Typography>
                    <Typography variant="caption" display="block">
                      Lotes vencendo: <b>{u.qtd_lotes_vencendo}</b> &nbsp; Vencidos: <b>{u.qtd_lotes_vencidos}</b>
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      fullWidth
                      sx={{ mt: 1 }}
                      onClick={() => handleVerLotes(u)}
                    >
                      Ver lotes
                    </Button>
                  </Box>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Legenda flutuante */}
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              p: 1.5,
              borderRadius: 2,
              zIndex: 1000,
              bgcolor: 'rgba(255,255,255,0.95)',
            }}
          >
            <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
              Status de criticidade
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: CORES_STATUS.ADEQUADO, borderRadius: '50%' }} />
              <Typography variant="caption">Adequado</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: CORES_STATUS.BAIXO, borderRadius: '50%' }} />
              <Typography variant="caption">Baixo</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: CORES_STATUS.FALTA, borderRadius: '50%' }} />
              <Typography variant="caption">Falta</Typography>
            </Box>
          </Paper>
        </Box>
      </Paper>

      {/* Tabela de lotes */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventBusyIcon color="warning" />
            <Typography variant="h6" fontWeight={600}>
              {unidadeSelecionada ? `Lotes da unidade ${unidadeSelecionada.nome_unidade}` : 'Lotes em alerta de validade'}
            </Typography>
          </Box>
          {unidadeSelecionada && (
            <Button size="small" onClick={limparFiltroUnidade}>
              Mostrar alertas gerais
            </Button>
          )}
        </Box>
        {loadingLotes ? (
          <Box sx={{ p: 3 }}>
            <LinearProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Medicamento</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Lote</TableCell>
                  {!unidadeSelecionada && <TableCell sx={{ fontWeight: 700 }}>Unidade</TableCell>}
                  <TableCell sx={{ fontWeight: 700 }}>Fabricante</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Quantidade</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Validade</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Dias</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Situacao</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lotesParaTabela.map((l) => {
                  const c = corChip(l.dias_para_vencer);
                  return (
                    <TableRow key={l.id} hover>
                      <TableCell>{l.nome_medicamento}</TableCell>
                      <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{l.lote}</Typography></TableCell>
                      {!unidadeSelecionada && <TableCell>{l.nome_unidade || '-'}</TableCell>}
                      <TableCell>{l.fabricante}</TableCell>
                      <TableCell align="right">{l.quantidade_disponivel.toLocaleString('pt-BR')}</TableCell>
                      <TableCell>{formatDate(l.data_validade)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={c.label} sx={{ bgcolor: c.bg, color: '#fff', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>{l.situacao}</TableCell>
                    </TableRow>
                  );
                })}
                {lotesParaTabela.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={unidadeSelecionada ? 7 : 8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Nenhum lote para exibir.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

// ============================================================
// ABA 3 - Cobertura REMUME / PCDTs (RF-19.2)
// ============================================================
const AbaCoberturaRemume: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<CoberturaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [filtroComponente, setFiltroComponente] = useState<string>('');
  const [filtroPcdtId, setFiltroPcdtId] = useState<number | ''>('');

  const fetchData = useCallback(async () => {
    if (!municipioAtivo) return;
    setLoading(true);
    setError(null);
    try {
      const filtros: any = {};
      if (filtroStatus) filtros.status = filtroStatus;
      if (filtroComponente) filtros.componente = filtroComponente;
      if (filtroPcdtId) filtros.pcdt_id = filtroPcdtId;
      const result = await monitoramentoService.getDispensacoesCoberturaRemume(
        municipioAtivo.id,
        Object.keys(filtros).length ? filtros : undefined,
      );
      setData(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar cobertura REMUME.');
    } finally {
      setLoading(false);
    }
  }, [municipioAtivo, filtroStatus, filtroComponente, filtroPcdtId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const limparFiltros = () => {
    setFiltroStatus('');
    setFiltroComponente('');
    setFiltroPcdtId('');
  };

  const handleSelectStatus = (e: SelectChangeEvent<string>) => setFiltroStatus(e.target.value);
  const handleSelectComponente = (e: SelectChangeEvent<string>) => setFiltroComponente(e.target.value);
  const handleSelectPcdt = (e: SelectChangeEvent<string>) => {
    const v = e.target.value;
    setFiltroPcdtId(v ? Number(v) : '');
  };

  const pieData = useMemo(() => {
    if (!data?.kpis) return [];
    return [
      { name: LABEL_COBERTURA.TOTAL, value: Number(data.kpis.com_estoque_total || 0), key: 'TOTAL' },
      { name: LABEL_COBERTURA.PARCIAL, value: Number(data.kpis.com_estoque_parcial || 0), key: 'PARCIAL' },
      { name: LABEL_COBERTURA.SEM_ESTOQUE, value: Number(data.kpis.sem_estoque || 0), key: 'SEM_ESTOQUE' },
    ].filter((d) => d.value > 0);
  }, [data]);

  if (loading) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  const kpis = data?.kpis;
  const medicamentos = data?.medicamentos || [];
  const pcdts = data?.pcdts || [];

  return (
    <Box>
      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Total REMUME" valor={(kpis?.total_remume ?? 0).toLocaleString('pt-BR')} cor="#1565c0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Cobertura Total" valor={(kpis?.com_estoque_total ?? 0).toLocaleString('pt-BR')} cor={CORES_COBERTURA.TOTAL} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Cobertura Parcial" valor={(kpis?.com_estoque_parcial ?? 0).toLocaleString('pt-BR')} cor={CORES_COBERTURA.PARCIAL} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard titulo="Sem Estoque" valor={(kpis?.sem_estoque ?? 0).toLocaleString('pt-BR')} cor={CORES_COBERTURA.SEM_ESTOQUE} />
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="filtro-status">Disponibilidade</InputLabel>
              <Select labelId="filtro-status" label="Disponibilidade" value={filtroStatus} onChange={handleSelectStatus}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="TOTAL">Cobertura total</MenuItem>
                <MenuItem value="PARCIAL">Cobertura parcial</MenuItem>
                <MenuItem value="SEM_ESTOQUE">Sem estoque</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="filtro-componente">Componente</InputLabel>
              <Select labelId="filtro-componente" label="Componente" value={filtroComponente} onChange={handleSelectComponente}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="BASICO">Basico</MenuItem>
                <MenuItem value="ESTRATEGICO">Estrategico</MenuItem>
                <MenuItem value="ESPECIALIZADO">Especializado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="filtro-pcdt">Protocolo Clinico (PCDT)</InputLabel>
              <Select labelId="filtro-pcdt" label="Protocolo Clinico (PCDT)" value={filtroPcdtId === '' ? '' : String(filtroPcdtId)} onChange={handleSelectPcdt}>
                <MenuItem value="">Todos</MenuItem>
                {pcdts.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>{p.codigo} - {p.titulo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={limparFiltros}>Limpar</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Pie + PCDTs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Distribuicao por Cobertura
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={CORES_COBERTURA[entry.key]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalPharmacyIcon color="primary" />
              Protocolos Clinicos Vinculados (PCDT)
            </Typography>
            <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {pcdts.map((p) => {
                const cobertura = p.total_medicamentos > 0
                  ? Math.round((Number(p.medicamentos_disponiveis) / Number(p.total_medicamentos)) * 100)
                  : 0;
                const corBadge = cobertura >= 80 ? CORES_COBERTURA.TOTAL : cobertura >= 40 ? CORES_COBERTURA.PARCIAL : CORES_COBERTURA.SEM_ESTOQUE;
                return (
                  <Accordion key={p.id} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>{p.codigo} - {p.titulo}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            CID {p.doenca_cid} | {p.nivel_atencao}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={`${p.medicamentos_disponiveis}/${p.total_medicamentos} (${cobertura}%)`}
                          sx={{ bgcolor: corBadge, color: '#fff', fontWeight: 600 }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {(p.medicamentos || []).map((m) => (
                          <Box key={m.remume_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">{m.nome}</Typography>
                            <Chip
                              size="small"
                              label={`${Number(m.percentual_cobertura).toFixed(0)}%`}
                              sx={{ bgcolor: CORES_COBERTURA[m.status_cobertura], color: '#fff' }}
                            />
                          </Box>
                        ))}
                        {(p.medicamentos || []).length === 0 && (
                          <Typography variant="caption" color="text.secondary">Sem medicamentos REMUME vinculados.</Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
              {pcdts.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Nenhum protocolo vinculado.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela REMUME */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>Catalogo REMUME e Cobertura Municipal</Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>Medicamento</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Componente</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Classe Terapeutica</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Cobertura</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Unidades</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>PCDTs Vinculados</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {medicamentos.map((m) => (
                <TableRow key={m.remume_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{m.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.principio_ativo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={m.componente} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{m.classe_terapeutica}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, Number(m.percentual_cobertura))}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': { bgcolor: CORES_COBERTURA[m.status_cobertura] },
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ minWidth: 36 }}>{Number(m.percentual_cobertura).toFixed(0)}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">{m.unidades_com_estoque}/{m.total_unidades}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(m.pcdts || []).map((p) => (
                        <Chip
                          key={p.id}
                          size="small"
                          label={p.codigo}
                          variant="outlined"
                          clickable
                          onClick={() => setFiltroPcdtId(p.id)}
                          title={p.titulo}
                        />
                      ))}
                      {(m.pcdts || []).length === 0 && (
                        <Typography variant="caption" color="text.disabled">-</Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {medicamentos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Nenhum medicamento corresponde aos filtros selecionados.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

// ============================================================
// WRAPPER - 3 abas
// ============================================================
const MonitoramentoDispensacoes: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [tab, setTab] = useState(0);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Typography variant="h5" color="text.secondary">Nenhum municipio selecionado</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Selecione um municipio para visualizar as dispensacoes.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
        Farmacia / Dispensacoes
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Dispensacoes" />
        <Tab label="Estoque & Lotes" />
        <Tab label="Cobertura REMUME / PCDTs" />
      </Tabs>

      {tab === 0 && <AbaDispensacoes />}
      {tab === 1 && <AbaEstoqueLotes />}
      {tab === 2 && <AbaCoberturaRemume />}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify({ aba: tab })}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoDispensacoes;
